import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useAdminCheck } from '@/hooks/useAdminCheck';

interface CompetitiveEvent {
  id: string;
  name: string;
  description?: string;
  start_date: string;
  end_date: string;
  target_votes: number;
  winner_count: number;
  prestige_days: number;
  is_active: boolean;
  is_finished: boolean;
  created_at: string;
  updated_at: string;
}

interface CompetitiveEventParticipation {
  id: string;
  event_id: string;
  server_id: string;
  votes_count: number;
  final_rank?: number;
  has_won: boolean;
  prestige_awarded: boolean;
  created_at: string;
  updated_at: string;
}

export const useCompetitiveEvents = () => {
  const queryClient = useQueryClient();
  const { isAdmin } = useAdminCheck();

  // Récupérer tous les événements (admin only)
  const { data: allEvents, isLoading: isLoadingAll } = useQuery({
    queryKey: ['competitive-events-admin'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('competitive_events' as any)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as any[];
    },
    enabled: isAdmin,
  });

  // Récupérer l'événement actif (public)
  const { data: activeEvent, isLoading: isLoadingActive } = useQuery({
    queryKey: ['competitive-events-active'],
    queryFn: async () => {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('competitive_events' as any)
        .select('*')
        .eq('is_active', true)
        .eq('is_finished', false)
        .lte('start_date', now)
        .gte('end_date', now)
        .maybeSingle();

      if (error) throw error;
      return data as any | null;
    },
  });

  // Récupérer l'événement à venir (public)
  const { data: upcomingEvent, isLoading: isLoadingUpcoming } = useQuery({
    queryKey: ['competitive-events-upcoming'],
    queryFn: async () => {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('competitive_events' as any)
        .select('*')
        .eq('is_active', true)
        .eq('is_finished', false)
        .gt('start_date', now)
        .order('start_date', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as any | null;
    },
  });

  // Récupérer les participations pour un événement
  const getEventParticipations = async (eventId: string) => {
    const { data, error } = await supabase
      .from('competitive_event_participations' as any)
      .select(`
        *,
        servers!inner (
          id,
          name,
          logo_image_url,
          games (
            name
          )
        )
      `)
      .eq('event_id', eventId)
      .order('votes_count', { ascending: false });

    if (error) throw error;
    return data;
  };

  // Créer un événement
  const createEvent = useMutation({
    mutationFn: async (eventData: any) => {
      const { data, error } = await supabase
        .from('competitive_events' as any)
        .insert(eventData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: "✅ Événement créé",
        description: "L'événement compétitif a été créé avec succès",
      });
      queryClient.invalidateQueries({ queryKey: ['competitive-events-admin'] });
      queryClient.invalidateQueries({ queryKey: ['competitive-events-active'] });
    },
    onError: (error: any) => {
      toast({
        title: "❌ Erreur",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Mettre à jour un événement
  const updateEvent = useMutation({
    mutationFn: async ({ id, ...updates }: any) => {
      const { data, error } = await supabase
        .from('competitive_events' as any)
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: "✅ Événement mis à jour",
        description: "L'événement a été modifié avec succès",
      });
      queryClient.invalidateQueries({ queryKey: ['competitive-events-admin'] });
      queryClient.invalidateQueries({ queryKey: ['competitive-events-active'] });
    },
    onError: (error: any) => {
      toast({
        title: "❌ Erreur",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Finaliser un événement
  const finalizeEvent = useMutation({
    mutationFn: async (eventId: string) => {
      const { data, error } = await supabase.rpc('finalize_competitive_event' as any, {
        p_event_id: eventId
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: "✅ Événement finalisé",
        description: "L'événement a été terminé et les récompenses distribuées",
      });
      queryClient.invalidateQueries({ queryKey: ['competitive-events-admin'] });
      queryClient.invalidateQueries({ queryKey: ['competitive-events-active'] });
    },
    onError: (error: any) => {
      toast({
        title: "❌ Erreur",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    // Données
    allEvents,
    activeEvent,
    upcomingEvent,
    isLoadingAll,
    isLoadingActive,
    isLoadingUpcoming,
    
    // Actions
    createEvent,
    updateEvent,
    finalizeEvent,
    getEventParticipations,
  };
};